import React from 'react';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { MetricText } from '@atlaskit/primitives/compiled/metric-text';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import InfoPanel from '@atlassian/analytics-info-panel/info-panel';

import { POPULAR } from '@confluence/named-routes/entry-points/routes.ts';

import { TestId } from '../dialogConstants.tsx';

import * as S from './styled.tsx';

export type AnalyticsDialogInfoPanelProps = {
	isCompactLayout?: boolean;
	isPopularContent?: boolean;
	isLoading: boolean;
	viewsCount: number;
	uniqueViewsCount: number;
};

const styles = cssMap({
	popular: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.025'),
		font: token('font.body.small'),
		color: token('color.text.success'),
		textDecoration: 'none',
		'&:visited': { color: token('color.text.success') },
		'&:hover': { color: token('color.text.success'), textDecoration: 'none' },
	},
	compact: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		marginBottom: token('space.150'),
		overflowWrap: 'anywhere',
	},
});

const CompactInfoPanel: FC<Omit<AnalyticsDialogInfoPanelProps, 'isCompactLayout'>> = ({
	isLoading,
	isPopularContent,
	viewsCount,
	uniqueViewsCount,
}) => (
	<Stack space="space.025" xcss={styles.compact}>
		{isLoading ? (
			<>
				<Skeleton width="60%" height="20px" />
				<Skeleton width="40%" height="16px" />
			</>
		) : (
			<>
				<MetricText size="small">
					<FormattedMessage
						id="confluence-analytics.dialog.compact.unique-viewers"
						defaultMessage="{count, plural, one {# unique viewer} other {# unique viewers}}"
						description="Number of unique viewers in the compact analytics popup."
						values={{ count: uniqueViewsCount }}
					/>
				</MetricText>
				<Inline space="space.100" alignBlock="center" shouldWrap>
					<Text size="small" color="color.text.subtlest">
						<FormattedMessage
							id="confluence-analytics.dialog.compact.total-views"
							defaultMessage="{count, plural, one {# total view} other {# total views}}"
							description="Total number of views in the compact analytics popup."
							values={{ count: viewsCount }}
						/>
					</Text>
					{isPopularContent && (
						<Anchor
							href={POPULAR.pattern}
							target="_blank"
							rel="noopener noreferrer"
							xcss={styles.popular}
						>
							<ChartTrendUpIcon label="" size="small" color={token('color.text.success')} />
							<FormattedMessage
								id="confluence-analytics.dialog.compact.popular"
								defaultMessage="Popular"
								description="Link to popular content next to the total views in the compact analytics popup."
							/>
						</Anchor>
					)}
				</Inline>
			</>
		)}
	</Stack>
);

export const AnalyticsDialogInfoPanel: FC<AnalyticsDialogInfoPanelProps> = ({
	isCompactLayout = false,
	isLoading,
	isPopularContent,
	viewsCount,
	uniqueViewsCount,
}) => {
	if (isCompactLayout) {
		return (
			<CompactInfoPanel
				isLoading={isLoading}
				isPopularContent={isPopularContent}
				viewsCount={viewsCount}
				uniqueViewsCount={uniqueViewsCount}
			/>
		);
	}

	return (
		<S.InfoPanelRow>
			<S.InfoPanelContainer>
				<InfoPanel
					value={viewsCount}
					metric={
						<FormattedMessage
							id="confluence-analytics.metrics.views-all-time"
							defaultMessage="views (all-time)"
							description="Name of a metric."
						/>
					}
					isLoading={isLoading}
					testId={TestId.INFO_PANEL_1}
				/>
			</S.InfoPanelContainer>
			<S.InfoPanelContainer>
				<InfoPanel
					value={uniqueViewsCount}
					metric={
						<FormattedMessage
							id="confluence-analytics.metrics.unique-viewers-all-time"
							defaultMessage="unique viewers (all-time)"
							description="Name of a metric."
						/>
					}
					isLoading={isLoading}
					testId={TestId.INFO_PANEL_2}
				/>
			</S.InfoPanelContainer>
		</S.InfoPanelRow>
	);
};
